<!--
  --
  -- オプション選択ページ
  --
  -->
<template>
  <c-container form wide>
    <c-book-header>オプションを選択</c-book-header>

    <c-book-summary-sheet/>

    <c-form card-style
      v-model="valid"
      @submit="onSubmit"
    >
      <v-card-text>

        <!-- 免責補償 -->
        <template v-if="insurance.items.length > 0">
          <c-insurance-sheet caption="保険・補償制度" :insurance="insurance" />
        </template>

        <!-- フライト便名 -->
        <template v-if="services.flights.enabled">
          <v-divider class="my-2"/>
          <c-flight-sheet caption="フライト便名" :flights="services.flights" />
        </template>

        <!-- オプション -->
        <template v-for="option in options">
          <v-divider :key="`divider:${option.id}`" class="my-2"/>
          <c-option-sheet :key="`sheet:${option.id}`" :option="option"/>
        </template>

        <!-- 送迎 -->
        <template v-if="services.pick.enabled">
          <v-divider class="my-2"/>
          <c-pick-sheet caption="送迎サービス" placeLabel="送迎先" :service="services.pick" />
        </template>

        <!-- 配車 -->
        <template v-if="services.deli.enabled">
          <v-divider class="my-2"/>
          <c-pick-sheet caption="配車サービス" placeLabel="配車先" :service="services.deli" />
        </template>

        <!-- 乗り捨て -->
        <template v-if="services.returns.enabled">
          <v-divider class="my-2"/>
          <c-returns-sheet caption="乗り捨てサービス" :returns="services.returns" />
        </template>

      </v-card-text>

      <v-card-actions>
        <v-spacer/>

        <v-btn
          color="primary" type="submit"
          :loading="running"
          :disabled="!valid || running"
        >
          次へ
        </v-btn>
      </v-card-actions>

    </c-form>
  </c-container>
</template>

<script>
import page from '@/mixins/page'
import CBookHeader from '@/pages/book/components/CBookHeader'
import CBookSummarySheet from '@/pages/book/components/CBookSummarySheet'
import CForm from '@/components/CForm'
import CInsuranceSheet from './CInsuranceSheet'
import COptionSheet from './COptionSheet'
import CPickSheet from './CPickSheet'
import CFlightSheet from './CFlightSheet'
import CReturnsSheet from './CReturnsSheet'

export default {
  name: 'Options',
  mixins: [
    page('オプションを選択')
  ],
  components: {
    CBookHeader,
    CBookSummarySheet,
    CForm,
    CInsuranceSheet,
    COptionSheet,
    CPickSheet,
    CFlightSheet,
    CReturnsSheet
  },

  async prepare (to, from, store) {
    // 各情報をストアから取得する
    const results = await Promise.all([
      // オプション
      store.getters['book/getOptions'](),

      // 免責補償
      store.getters['book/getInsurance'](),

      // サービス
      store.getters['book/getServices']()
    ])

    // setData() に渡す
    return {
      prepared: {
        options: results[0],
        insurance: results[1],
        services: results[2]
      }
    }
  },

  props: {
    prepared: Object
  },

  data () {
    return {
      // 各種入力値
      options: this.prepared.options,
      insurance: this.prepared.insurance,
      services: this.prepared.services,

      // フォームのバリデーション状態
      valid: true,

      // 処理中
      running: false
    }
  },

  methods: {
    onSubmit () {
      // オプションを保存する
      this.commit('book/setOptions', getSaveOptions(this.options))

      // 免責補償を保存する
      this.commit('book/setInsurance', getSavedInsurance(this.insurance))

      // 送迎を保存する
      this.commit('book/setPick', getSavedPick(this.services.pick))

      // 配車を保存する
      this.commit('book/setDeli', getSavedPick(this.services.deli))

      // 乗り捨てを保存する
      this.commit('book/setReturns', getSavedReturns(this.services.returns))

      // フライトを保存する
      this.commit('book/setFlights', getSavedFlights(this.services.flights))

      // 次のページへ
      this.goTo({ name: 'book/remarks' })
    }
  }
}

/**
 * ストアに保存するオプション群データを取得する
 *
 * @param {Array} options オプションリスト
 * @returns {Array} ストアに保存するオプション群データを返す
 */
function getSaveOptions (options) {
  // 数量の入力があったものだけを抽出する
  options = options.filter(item => item.quantity > 0)

  // ストアに保存する形式に変換する
  return options.map(item => ({
    // 基本情報
    id: item.id,
    name: item.name,

    // 数量
    quantity: item.quantity,
    unit: item.unit,

    // 料金計算に必要な情報
    chargeType: item.chargeType,
    maxDays: item.maxDays,
    unitCharge: item.unitCharge
  }))
}

/**
 * ストアに保存する免責補償データを取得する
 *
 * @param {Object} insurance 免責補償データ
 * @returns {Object} ストアに保存する免責補償データを返す
 */
function getSavedInsurance (insurance) {
  const selected = insurance.items.find(item => item.id === insurance.value)
  if (!selected) {
    return {
      id: 0,
      name: '',
      unitCharge: 0,
      maxDays: Infinity
    }
  }

  return {
    id: selected.id,
    name: selected.name,
    unitCharge: selected.unitCharge,
    maxDays: selected.maxDays
  }
}

/**
 * ストアに保存する送迎・配車データを取得する
 *
 * @param {Object} service 送迎・配車データ
 * @returns {Object} ストアに保存する送迎・配車データを返す
 */
function getSavedPick (service) {
  return {
    value: service.value,
    place: service.place,
    oneway: service.oneway,
    round: service.round
  }
}

/**
 * ストアに保存する乗り捨てデータを取得する
 *
 * @param {Object} service 乗り捨てデータ
 * @returns {Object} ストアに保存する乗り捨てデータを返す
 */
function getSavedReturns (returns) {
  // 返却先ステーション情報を取得する
  const station = returns.stations.find(item => item.stationID === returns.stationID)

  return {
    station
  }
}

/**
 * ストアに保存するフライトデータを取得する
 *
 * @param {Object} flights フライトデータ
 * @returns {Object} ストアに保存するフライトデータを返す
 */
function getSavedFlights (flights) {
  return {
    decided: flights.decided,
    arrival: flights.arrival,
    departure: flights.departure
  }
}

</script>
